// pages/grant-success.ts — confirmation screen shown immediately after
// a grant application is submitted.
//
// The previous page (GrantForm) navigates here with the freshly-returned
// grant object stashed via the router's pageData mechanism:
//   ctx.navigate('grant-success', { id: g.GrantId }, { grant: g });
//
// We render straight off ctx.pageData. If the user lands here directly
// (refresh, deep link), pageData is null and we fall back to a small
// "no submission found" card.

import type { Page, PageContext } from '../router';
import { html, esc, fmtMoney, fmtMoneyOrDash } from '../util';

interface SubmittedGrant {
  GrantId: number;
  ReferenceNumber: string;
  RequestedAmount: number | null;
  OrganizationLegalName: string;
  SubmittedAt: string;
}

const GrantSuccessPage: Page = {
  guard(ctx: PageContext): string | null {
    return ctx.user ? null : 'login';
  },

  render(ctx: PageContext): string {
    const grant = (ctx.pageData as { grant?: SubmittedGrant } | null)?.grant;
    if (!grant) {
      return html`
        <div class="wrap" style="max-width: 600px; padding: 60px 24px; text-align: center;">
          <p class="t-body">No submission found.</p>
          <a class="btn btn-primary" data-nav="user-dashboard">Back to dashboard</a>
        </div>
      `;
    }

    const submittedStr = grant.SubmittedAt
      ? new Date(grant.SubmittedAt).toLocaleString('en-US', { dateStyle: 'long', timeStyle: 'short' })
      : '\u2014';

    const rows: Array<[string, string]> = [
      ['Amount requested', fmtMoneyOrDash(grant.RequestedAmount)],
      ['Organization',     grant.OrganizationLegalName],
      ['Submitted',        submittedStr],
      ['Expected decision','Within 4 weeks'],
    ];

    const rowsHtml = rows.map(([k, v]) => html`
      <div class="d-flex" style="justify-content: space-between; margin-bottom: 8px; font-size: 14px;">
        <span class="t-small">${k}</span>
        <span style="font-weight: 500;">${v}</span>
      </div>
    `).join('');

    return html`
      <div class="wrap" style="max-width: 640px; padding: 60px 24px; text-align: center;">
        <div class="gs-check">✓</div>

        <div class="eyebrow" style="display: inline-block;">Submitted</div>
        <h1 class="t-h1" style="margin: 16px 0 12px; font-size: clamp(40px, 5vw, 64px);">
          Application submitted.
        </h1>
        <p class="t-body" style="margin-bottom: 28px;">
          Your grant request has been received. Our team will begin their review shortly.
        </p>

        <div class="card" style="margin-bottom: 24px; text-align: left; padding: 24px;">
          <div style="text-align: center; margin-bottom: 20px;">
            <p class="t-small">Your reference number</p>
            <div class="serif" style="font-size: 30px; color: var(--primary); margin-top: 4px;">
              ${grant.ReferenceNumber}
            </div>
          </div>
          <div style="background: var(--paper-2); border-radius: 10px; padding: 14px 16px; margin-bottom: 16px;">
            ${{ __raw: rowsHtml }}
          </div>
          <div class="gs-alert">
            <strong>Important:</strong> We can only provide grants to registered 501(c)(3) organizations. If your organization does not have 501(c)(3) status, your application will be denied.
          </div>
        </div>

        <div class="d-flex" style="gap: 12px; justify-content: center; flex-wrap: wrap;">
          <a class="btn btn-primary btn-lg" data-nav="user-dashboard">View my dashboard</a>
          <a class="btn btn-outline-primary" data-nav="grant-form">Submit another</a>
        </div>
      </div>
    `;
  },
};

void esc; void fmtMoney;

export default GrantSuccessPage;
